import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  IClubResourceData,
  ClubResourceListQueryParams,
  PriceUnit,
  RecurrencePattern,
  ResourceType,
  SeasonalDates,
  IPortWatchShiftsResponse,
  IAssignShiftPayload,
  SaunaWeeklyWindows,
  SaunaSpecialDateWindow,
  DutyLiabilitySummaryResponse,
} from '@/types/club-resource.types';

export interface Inspector {
  id: string;
  name: string;
  email: string;
  user_id: string;
}

export interface GetAvailableInspectorsParams {
  date?: string; // YYYY-MM-DD format
  start_time?: string; // HH:MM format
  end_time?: string; // HH:MM format
  club_id?: string;
}

export interface UpdateInspectionSlotPayload {
  date: string;
  start_time: string;
  end_time: string;
  inspector_id: string;
}

interface BackendInspectorResponse {
  status: boolean;
  message: string;
  data: Inspector[];
}

interface BackendClubResourceData {
  id?: string;
  club_id?: string;
  name: string;
  description?: string;
  resource_type?: ResourceType;
  allow_bookings?: boolean;
  slot_length?: string;
  quantity?: number;
  recurrence_pattern?: RecurrencePattern;
  max_bookings_period_days?: number;
  price?: number | null;
  price_unit?: PriceUnit | null;
  seasonal_dates?: SeasonalDates;
  // Sauna-specific fields
  weekly_windows?: SaunaWeeklyWindows;
  special_date_windows?: SaunaSpecialDateWindow[];
  closed_dates?: string[];
  status: number;
  created_at?: string;
  updated_at?: string;
  translations?: {
    [languageCode: string]: {
      description?: string;
    };
  };
}

interface BackendPaginatedClubResourceResponse {
  status: boolean;
  message: string;
  data: BackendClubResourceData[];
  total: number;
  totalPages: number;
  currentPage: number;
}

interface BackendSingleClubResourceResponse {
  status: boolean;
  message: string;
  data: BackendClubResourceData;
}

export const ClubResourceService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getClubResources: builder.query<
      PaginatedApiResponse<IClubResourceData[]>,
      ClubResourceListQueryParams
    >({
      query: (params) => ({
        url: 'club-resources',
        method: 'GET',
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          status: params.status,
          club_id: params.club_id,
        },
      }),
      transformResponse: (response: BackendPaginatedClubResourceResponse) => {
        // Map backend field names to frontend field names
        const mappedData = response.data?.map(
          (item: BackendClubResourceData) => ({
            ...item,
            allow_booking: item.allow_bookings, // Map allow_bookings to allow_booking
            define_price: item.price !== null && item.price !== undefined, // Derive define_price from price existence
          })
        );

        return {
          status: response.status,
          message: response.message,
          data: mappedData || [],
          total: response.total || mappedData?.length || 0,
          totalPages: response.totalPages || 1,
          currentPage: response.currentPage || 1,
        };
      },
      providesTags: ['ClubResources'],
      keepUnusedDataFor: 0,
    }),

    getClubResourceById: builder.query<IClubResourceData, string>({
      query: (id) => ({
        url: `club-resources/${id}`,
        method: 'GET',
      }),
      transformResponse: (
        response: BackendSingleClubResourceResponse | BackendClubResourceData
      ) => {
        // Map backend field names to frontend field names
        const data =
          (response as BackendSingleClubResourceResponse).data ||
          (response as BackendClubResourceData);
        return {
          ...data,
          allow_booking: data.allow_bookings, // Map allow_bookings to allow_booking
          define_price: data.price !== null && data.price !== undefined, // Derive define_price from price existence
        };
      },
      providesTags: (result, error, id) => [{ type: 'ClubResources', id }],
      keepUnusedDataFor: 0,
    }),

    createClubResource: builder.mutation<
      ApiResponse<IClubResourceData>,
      Partial<IClubResourceData>
    >({
      query: (data) => {
        // Map frontend field names to backend field names
        const payload = {
          ...data,
          allow_bookings: data.allow_booking, // Map allow_booking to allow_bookings
        };

        // If price configuration is explicitly disabled, clear price fields so backend removes them
        if (data.define_price === false) {
          payload.price = null;
          payload.price_unit = null;
        }

        // Remove frontend-only fields (but keep sauna-specific fields)
        delete payload.allow_booking;
        delete payload.define_price;

        return {
          url: 'club-resources',
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: ['ClubResources'],
    }),

    updateClubResource: builder.mutation<
      ApiResponse<IClubResourceData>,
      { id: string; data: Partial<IClubResourceData> }
    >({
      query: ({ id, data }) => {
        // Map frontend field names to backend field names
        const payload = {
          ...data,
          allow_bookings: data.allow_booking, // Map allow_booking to allow_bookings
        };

        // If price configuration is explicitly disabled, clear price fields so backend removes them on update
        if (data.define_price === false) {
          payload.price = null;
          payload.price_unit = null as unknown as typeof data.price_unit;
        }

        // Remove frontend-only fields (but keep sauna-specific fields)
        delete payload.allow_booking;
        delete payload.define_price;

        return {
          url: `club-resources/${id}`,
          method: 'PATCH',
          data: payload,
        };
      },
      invalidatesTags: ['ClubResources'],
    }),

    deleteClubResource: builder.mutation<ApiResponse<null>, string>({
      query: (id) => ({
        url: `club-resources/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ClubResources'],
    }),

    getPortWatchShifts: builder.query<IPortWatchShiftsResponse, string>({
      query: (resourceId) => ({
        url: `club-resource-bookings/port-watch-shifts/${resourceId}`,
        method: 'GET',
      }),
      providesTags: (result, error, resourceId) => [
        { type: 'ClubResources', id: `shifts-${resourceId}` },
      ],
    }),

    // Fetch port watch shifts for a club (filled and completed)
    getPortWatchShiftsByClub: builder.query<IPortWatchShiftsResponse, string>({
      query: (clubId) => ({
        url: `club-resource-bookings/port-watch-shifts/club/${clubId}`,
        method: 'GET',
      }),
      providesTags: (result, error, clubId) => [
        { type: 'ClubResources', id: `shifts-club-${clubId}` },
      ],
    }),

    // Duty liability summary for a club's port watch shifts
    getDutyLiabilityByClub: builder.query<DutyLiabilitySummaryResponse, string>(
      {
        query: (clubId) => ({
          url: `club-resource-bookings/port-watch-shifts/club/${clubId}/duty-liability`,
          method: 'GET',
        }),
        providesTags: (result, error, clubId) => [
          { type: 'ClubResources', id: `duty-liability-${clubId}` },
        ],
      }
    ),

    assignShiftMember: builder.mutation<
      ApiResponse<null>,
      { shiftId: string; payload: IAssignShiftPayload }
    >({
      query: ({ shiftId, payload }) => ({
        url: `club-resource-bookings/port-watch-shifts/${shiftId}/assign`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: (result, error, { shiftId }) => [
        { type: 'ClubResources', id: `shifts-${shiftId}` },
      ],
    }),

    unassignShiftMember: builder.mutation<ApiResponse<null>, string>({
      query: (shiftId) => ({
        url: `club-resource-bookings/port-watch-shifts/${shiftId}/unassign`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, shiftId) => [
        { type: 'ClubResources', id: `shifts-${shiftId}` },
      ],
    }),

    completeShift: builder.mutation<ApiResponse<null>, string>({
      query: (shiftId) => ({
        url: `club-resource-bookings/port-watch-shifts/${shiftId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, shiftId) => [
        { type: 'ClubResources', id: `shifts-${shiftId}` },
      ],
    }),

    getAvailableInspectors: builder.query<
      Inspector[],
      GetAvailableInspectorsParams
    >({
      query: (params) => ({
        url: 'club-resources/club/available-inspectors',
        method: 'GET',
        params: {
          date: params.date || undefined,
          start_time: params.start_time || undefined,
          end_time: params.end_time || undefined,
          club_id: params.club_id || undefined,
        },
      }),
      transformResponse: (response: BackendInspectorResponse) => {
        return response.data || [];
      },
      keepUnusedDataFor: 0, // Don't cache inspector availability data
    }),

    translateClubResourceContent: builder.mutation<
      {
        translations: Record<
          string,
          {
            description?: string;
          }
        >;
      },
      {
        description?: string;
        source_language?: string;
        resource_id?: string;
      }
    >({
      query: (data) => ({
        url: 'harbors/translate',
        method: 'POST',
        data,
      }),
      transformResponse: (
        response:
          | {
              status?: boolean;
              message?: string;
              translations?: Record<
                string,
                {
                  description?: string;
                }
              > | null;
              data?: Record<
                string,
                {
                  description?: string;
                }
              > | null;
            }
          | Record<
              string,
              {
                description?: string;
              }
            >
      ) => {
        type TranslationMap = Record<
          string,
          {
            description?: string;
          }
        >;

        const maybeWrapper = response as {
          translations?: TranslationMap | null;
          data?: TranslationMap | null;
        };

        let languageMap: TranslationMap | undefined = undefined;
        if (maybeWrapper && typeof maybeWrapper === 'object') {
          if ('translations' in maybeWrapper && maybeWrapper.translations) {
            languageMap = maybeWrapper.translations || ({} as TranslationMap);
          } else if ('data' in maybeWrapper && maybeWrapper.data) {
            languageMap = maybeWrapper.data || ({} as TranslationMap);
          }
        }

        if (!languageMap) {
          languageMap = (response as TranslationMap) || ({} as TranslationMap);
        }

        return { translations: languageMap };
      },
    }),

    updateInspectionSlot: builder.mutation<
      ApiResponse<null>,
      { id: string; data: UpdateInspectionSlotPayload }
    >({
      query: ({ id, data }) => ({
        url: `club-resources/inspection-slots/${id}`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['ClubResources'],
    }),
  }),
});

export const {
  useGetClubResourcesQuery,
  useGetClubResourceByIdQuery,
  useCreateClubResourceMutation,
  useUpdateClubResourceMutation,
  useDeleteClubResourceMutation,
  useGetPortWatchShiftsQuery,
  useGetPortWatchShiftsByClubQuery,
  useGetDutyLiabilityByClubQuery,
  useAssignShiftMemberMutation,
  useUnassignShiftMemberMutation,
  useCompleteShiftMutation,
  useGetAvailableInspectorsQuery,
  useTranslateClubResourceContentMutation,
  useUpdateInspectionSlotMutation,
} = ClubResourceService;
